/**
 * IndexedDB persistence for chapter drafts + sync outbox + parked conflicts.
 * Falls back to an in-memory Map when IndexedDB is unavailable (SSR / tests).
 */

import {
  DEFAULT_CHAPTER_JOURNAL_RETENTION,
  type ChapterDraftRecord,
  type ChapterJournalEntry,
  type ChapterJournalRetentionPolicy,
  type ChapterOutboxItem,
  type PendingConflictRecord,
} from '../types';

const DB_NAME = 'justwrite-chapter-sync';
const DB_VERSION = 3;
const STORE_DRAFTS = 'drafts';
const STORE_OUTBOX = 'outbox';
const STORE_META = 'meta';
const STORE_CONFLICTS = 'conflicts';
const STORE_JOURNAL = 'journal';
const JOURNAL_BOOK_INDEX = 'byBook';
const JOURNAL_CHAPTER_INDEX = 'byChapter';

export type ChapterSyncStorage = {
  getDraft(userId: string, bookId: string, chapterId: string): Promise<ChapterDraftRecord | null>;
  putDraft(draft: ChapterDraftRecord): Promise<void>;
  deleteDraft(userId: string, bookId: string, chapterId: string): Promise<void>;
  listDraftsForBook(userId: string, bookId: string): Promise<ChapterDraftRecord[]>;
  enqueueOutbox(item: ChapterOutboxItem): Promise<void>;
  listOutbox(userId: string): Promise<ChapterOutboxItem[]>;
  updateOutbox(item: ChapterOutboxItem): Promise<void>;
  deleteOutbox(id: string): Promise<void>;
  getOrCreateDeviceId(): Promise<string>;
  getPendingConflict(
    userId: string,
    bookId: string,
    chapterId: string
  ): Promise<PendingConflictRecord | null>;
  putPendingConflict(record: PendingConflictRecord): Promise<void>;
  deletePendingConflict(userId: string, bookId: string, chapterId: string): Promise<void>;
  migrateLocalStorageDrafts?(userId: string): Promise<number>;
  /** Append-only. Returns the stored entry, or null when a flush was coalesced. */
  appendJournal(
    entry: ChapterJournalInput,
    policy?: ChapterJournalRetentionPolicy
  ): Promise<ChapterJournalEntry | null>;
  listJournalForChapter(
    userId: string,
    bookId: string,
    chapterId: string
  ): Promise<ChapterJournalEntry[]>;
  listJournalForBook(userId: string, bookId: string): Promise<ChapterJournalEntry[]>;
  pruneJournal(
    userId: string,
    bookId: string,
    chapterId: string,
    policy?: ChapterJournalRetentionPolicy
  ): Promise<number>;
};

export type ChapterJournalInput = Omit<ChapterJournalEntry, 'key' | 'seq' | 'at'> & {
  at?: number;
};

function draftKey(userId: string, bookId: string, chapterId: string): string {
  return `${userId}::${bookId}::${chapterId}`;
}

function journalKey(entry: { userId: string; bookId: string; chapterId: string }, seq: number): string {
  return `${draftKey(entry.userId, entry.bookId, entry.chapterId)}::${String(seq).padStart(12, '0')}`;
}

function isFailureKind(kind: ChapterJournalEntry['kind']): boolean {
  return kind === 'push_failed' || kind === 'offline';
}

/**
 * A `flush` that lands within `checkpointEveryMs` of the previous `flush` is
 * dropped — the newer text supersedes it and we do not want one row per
 * keystroke. Anything that is not a plain flush is always kept.
 */
function shouldCoalesceFlush(
  entry: ChapterJournalInput,
  previous: ChapterJournalEntry | undefined,
  at: number,
  policy: ChapterJournalRetentionPolicy
): boolean {
  if (entry.kind !== 'flush') return false;
  if (!previous || previous.kind !== 'flush') return false;
  return at - previous.at < policy.checkpointEveryMs;
}

/**
 * Trim body payloads, never timeline entries, and never a failure — a failed or
 * undelivered save is the only local copy of that work. Bodies the server
 * already accepted are fair game. Returns the entries whose body was dropped.
 */
function applyRetention(
  entries: ChapterJournalEntry[],
  policy: ChapterJournalRetentionPolicy,
  now: number
): { changed: ChapterJournalEntry[] } {
  const changed: ChapterJournalEntry[] = [];
  const prunableNewestFirst = entries
    .filter((e) => !isFailureKind(e.kind) && e.contentJson !== undefined)
    .sort((a, b) => b.seq - a.seq);

  prunableNewestFirst.forEach((entry, index) => {
    const tooMany = index >= policy.maxBodiesPerChapter;
    const tooOld = now - entry.at > policy.maxBodyAgeMs;
    if (!tooMany && !tooOld) return;
    const { contentJson: _dropped, ...rest } = entry;
    changed.push(rest as ChapterJournalEntry);
  });

  return { changed };
}

function createMemoryStorage(): ChapterSyncStorage {
  const drafts = new Map<string, ChapterDraftRecord>();
  const outbox = new Map<string, ChapterOutboxItem>();
  const conflicts = new Map<string, PendingConflictRecord>();
  const journal = new Map<string, ChapterJournalEntry>();
  let deviceId =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `mem-${Date.now().toString(36)}`;

  return {
    async getDraft(userId, bookId, chapterId) {
      return drafts.get(draftKey(userId, bookId, chapterId)) ?? null;
    },
    async putDraft(draft) {
      drafts.set(draftKey(draft.userId, draft.bookId, draft.chapterId), draft);
    },
    async deleteDraft(userId, bookId, chapterId) {
      drafts.delete(draftKey(userId, bookId, chapterId));
    },
    async listDraftsForBook(userId, bookId) {
      return [...drafts.values()].filter((d) => d.userId === userId && d.bookId === bookId);
    },
    async enqueueOutbox(item) {
      outbox.set(item.id, item);
    },
    async listOutbox(userId) {
      return [...outbox.values()]
        .filter((o) => o.userId === userId)
        .sort((a, b) => a.createdAt - b.createdAt);
    },
    async updateOutbox(item) {
      outbox.set(item.id, item);
    },
    async deleteOutbox(id) {
      outbox.delete(id);
    },
    async getOrCreateDeviceId() {
      return deviceId;
    },
    async getPendingConflict(userId, bookId, chapterId) {
      return conflicts.get(draftKey(userId, bookId, chapterId)) ?? null;
    },
    async putPendingConflict(record) {
      conflicts.set(draftKey(record.userId, record.bookId, record.chapterId), record);
    },
    async deletePendingConflict(userId, bookId, chapterId) {
      conflicts.delete(draftKey(userId, bookId, chapterId));
    },
    async appendJournal(input, policy) {
      const at = input.at ?? Date.now();
      const existing = [...journal.values()]
        .filter(
          (e) =>
            e.userId === input.userId &&
            e.bookId === input.bookId &&
            e.chapterId === input.chapterId
        )
        .sort((a, b) => a.seq - b.seq);
      const previous = existing[existing.length - 1];
      if (
        shouldCoalesceFlush(input, previous, at, policy ?? DEFAULT_CHAPTER_JOURNAL_RETENTION)
      ) {
        return null;
      }
      const seq = (previous?.seq ?? 0) + 1;
      const entry: ChapterJournalEntry = { ...input, at, seq, key: journalKey(input, seq) };
      journal.set(entry.key, entry);
      return entry;
    },
    async listJournalForChapter(userId, bookId, chapterId) {
      return [...journal.values()]
        .filter((e) => e.userId === userId && e.bookId === bookId && e.chapterId === chapterId)
        .sort((a, b) => a.seq - b.seq);
    },
    async listJournalForBook(userId, bookId) {
      return [...journal.values()]
        .filter((e) => e.userId === userId && e.bookId === bookId)
        .sort((a, b) => a.at - b.at);
    },
    async pruneJournal(userId, bookId, chapterId, policy) {
      const entries = await this.listJournalForChapter(userId, bookId, chapterId);
      const { changed } = applyRetention(
        entries,
        policy ?? DEFAULT_CHAPTER_JOURNAL_RETENTION,
        Date.now()
      );
      for (const entry of changed) journal.set(entry.key, entry);
      return changed.length;
    },
  };
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_DRAFTS)) {
        db.createObjectStore(STORE_DRAFTS, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(STORE_OUTBOX)) {
        db.createObjectStore(STORE_OUTBOX, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META);
      }
      if (!db.objectStoreNames.contains(STORE_CONFLICTS)) {
        db.createObjectStore(STORE_CONFLICTS, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(STORE_JOURNAL)) {
        const os = db.createObjectStore(STORE_JOURNAL, { keyPath: 'key' });
        os.createIndex(JOURNAL_BOOK_INDEX, ['userId', 'bookId']);
        os.createIndex(JOURNAL_CHAPTER_INDEX, ['userId', 'bookId', 'chapterId']);
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

function idbReq<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
  });
}

function createIdbStorage(): ChapterSyncStorage {
  async function withStore<T>(
    store: string,
    mode: IDBTransactionMode,
    fn: (os: IDBObjectStore) => IDBRequest<T> | Promise<T>
  ): Promise<T> {
    const db = await openDb();
    const tx = db.transaction(store, mode);
    const os = tx.objectStore(store);
    const result = fn(os);
    if (result && typeof (result as IDBRequest).onsuccess !== 'undefined') {
      return idbReq(result as IDBRequest<T>);
    }
    return result as Promise<T>;
  }

  return {
    async getDraft(userId, bookId, chapterId) {
      const key = draftKey(userId, bookId, chapterId);
      const row = await withStore<{ key: string; draft: ChapterDraftRecord } | undefined>(
        STORE_DRAFTS,
        'readonly',
        (os) => os.get(key)
      );
      return row?.draft ?? null;
    },
    async putDraft(draft) {
      const key = draftKey(draft.userId, draft.bookId, draft.chapterId);
      await withStore(STORE_DRAFTS, 'readwrite', (os) => os.put({ key, draft }));
    },
    async deleteDraft(userId, bookId, chapterId) {
      const key = draftKey(userId, bookId, chapterId);
      await withStore(STORE_DRAFTS, 'readwrite', (os) => os.delete(key));
    },
    async listDraftsForBook(userId, bookId) {
      const db = await openDb();
      const tx = db.transaction(STORE_DRAFTS, 'readonly');
      const all = await idbReq(tx.objectStore(STORE_DRAFTS).getAll());
      return (all as Array<{ draft: ChapterDraftRecord }>)
        .map((r) => r.draft)
        .filter((d) => d && d.userId === userId && d.bookId === bookId);
    },
    async enqueueOutbox(item) {
      await withStore(STORE_OUTBOX, 'readwrite', (os) => os.put(item));
    },
    async listOutbox(userId) {
      const db = await openDb();
      const tx = db.transaction(STORE_OUTBOX, 'readonly');
      const all = (await idbReq(tx.objectStore(STORE_OUTBOX).getAll())) as ChapterOutboxItem[];
      return all.filter((o) => o.userId === userId).sort((a, b) => a.createdAt - b.createdAt);
    },
    async updateOutbox(item) {
      await withStore(STORE_OUTBOX, 'readwrite', (os) => os.put(item));
    },
    async deleteOutbox(id) {
      await withStore(STORE_OUTBOX, 'readwrite', (os) => os.delete(id));
    },
    async getOrCreateDeviceId() {
      const db = await openDb();
      const tx = db.transaction(STORE_META, 'readwrite');
      const os = tx.objectStore(STORE_META);
      const existing = await idbReq(os.get('deviceId'));
      if (typeof existing === 'string' && existing.trim()) return existing;
      const id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      await idbReq(os.put(id, 'deviceId'));
      return id;
    },
    async getPendingConflict(userId, bookId, chapterId) {
      const key = draftKey(userId, bookId, chapterId);
      const row = await withStore<
        { key: string; record: PendingConflictRecord } | undefined
      >(STORE_CONFLICTS, 'readonly', (os) => os.get(key));
      return row?.record ?? null;
    },
    async putPendingConflict(record) {
      const key = draftKey(record.userId, record.bookId, record.chapterId);
      await withStore(STORE_CONFLICTS, 'readwrite', (os) => os.put({ key, record }));
    },
    async deletePendingConflict(userId, bookId, chapterId) {
      const key = draftKey(userId, bookId, chapterId);
      await withStore(STORE_CONFLICTS, 'readwrite', (os) => os.delete(key));
    },
    async appendJournal(input, policy) {
      const at = input.at ?? Date.now();
      const existing = await this.listJournalForChapter(
        input.userId,
        input.bookId,
        input.chapterId
      );
      const previous = existing[existing.length - 1];
      if (
        shouldCoalesceFlush(input, previous, at, policy ?? DEFAULT_CHAPTER_JOURNAL_RETENTION)
      ) {
        return null;
      }
      const seq = (previous?.seq ?? 0) + 1;
      const entry: ChapterJournalEntry = { ...input, at, seq, key: journalKey(input, seq) };
      await withStore(STORE_JOURNAL, 'readwrite', (os) => os.put(entry));
      return entry;
    },
    async listJournalForChapter(userId, bookId, chapterId) {
      const db = await openDb();
      const tx = db.transaction(STORE_JOURNAL, 'readonly');
      const index = tx.objectStore(STORE_JOURNAL).index(JOURNAL_CHAPTER_INDEX);
      const all = (await idbReq(
        index.getAll(IDBKeyRange.only([userId, bookId, chapterId]))
      )) as ChapterJournalEntry[];
      return all.sort((a, b) => a.seq - b.seq);
    },
    async listJournalForBook(userId, bookId) {
      const db = await openDb();
      const tx = db.transaction(STORE_JOURNAL, 'readonly');
      const index = tx.objectStore(STORE_JOURNAL).index(JOURNAL_BOOK_INDEX);
      const all = (await idbReq(
        index.getAll(IDBKeyRange.only([userId, bookId]))
      )) as ChapterJournalEntry[];
      return all.sort((a, b) => a.at - b.at);
    },
    async pruneJournal(userId, bookId, chapterId, policy) {
      const entries = await this.listJournalForChapter(userId, bookId, chapterId);
      const { changed } = applyRetention(
        entries,
        policy ?? DEFAULT_CHAPTER_JOURNAL_RETENTION,
        Date.now()
      );
      if (changed.length === 0) return 0;
      const db = await openDb();
      const tx = db.transaction(STORE_JOURNAL, 'readwrite');
      const os = tx.objectStore(STORE_JOURNAL);
      for (const entry of changed) os.put(entry);
      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
      return changed.length;
    },
    async migrateLocalStorageDrafts(userId: string): Promise<number> {
      if (typeof localStorage === 'undefined') return 0;
      const prefix = 'jw_chapter_draft_v1_';
      let migrated = 0;
      const deviceId = await this.getOrCreateDeviceId();
      const keys: string[] = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const k = localStorage.key(i);
        if (k && k.startsWith(prefix)) keys.push(k);
      }
      for (const key of keys) {
        try {
          const raw = localStorage.getItem(key);
          if (!raw) continue;
          const parsed = JSON.parse(raw) as {
            bookId?: string;
            chapterId?: string;
            title?: string;
            contentJson?: string;
            savedAt?: number;
          };
          if (
            typeof parsed.bookId !== 'string' ||
            typeof parsed.chapterId !== 'string' ||
            typeof parsed.title !== 'string' ||
            typeof parsed.contentJson !== 'string'
          ) {
            continue;
          }
          const existing = await this.getDraft(userId, parsed.bookId, parsed.chapterId);
          const savedAt = typeof parsed.savedAt === 'number' ? parsed.savedAt : Date.now();
          if (existing && existing.savedAt >= savedAt) {
            localStorage.removeItem(key);
            continue;
          }
          await this.putDraft({
            bookId: parsed.bookId,
            chapterId: parsed.chapterId,
            userId,
            title: parsed.title,
            contentJson: parsed.contentJson,
            savedAt,
            baseRev: existing?.baseRev ?? 0,
            baseFingerprint: existing?.baseFingerprint ?? '',
            baseContentJson: existing?.baseContentJson ?? parsed.contentJson,
            baseTitle: existing?.baseTitle ?? parsed.title,
            deviceId,
          });
          localStorage.removeItem(key);
          migrated += 1;
        } catch {
          // ignore corrupt entries
        }
      }
      return migrated;
    },
  };
}

let singleton: ChapterSyncStorage | null = null;

/** Prefer IndexedDB in the browser; memory store otherwise (SSR / Node tests). */
export function getChapterSyncStorage(): ChapterSyncStorage {
  if (singleton) return singleton;
  if (typeof indexedDB !== 'undefined') {
    try {
      singleton = createIdbStorage();
      return singleton;
    } catch {
      // fall through
    }
  }
  singleton = createMemoryStorage();
  return singleton;
}

/** Test helper: force a fresh memory store. */
export function createMemoryChapterSyncStorage(): ChapterSyncStorage {
  return createMemoryStorage();
}

export function resetChapterSyncStorageForTests(): void {
  singleton = null;
}
